import ErrorIcon from '@mui/icons-material/Error';
import { Box } from '@mui/system';
import {
  DataGrid,
  DataGridProps,
  GridEventListener,
  GridFilterModel,
  GridOverlay,
  GridPaginationModel,
  GridRowParams,
  MuiEvent,
} from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { JsonParam, withDefault } from 'use-query-params';

import { bulkEditGridStyles, useSettingsDataGridStyles } from 'components/atoms/BaseDataGrid/Style';
import { ADMIN_TABLE_DEFAULT_PAGE_SIZE, ADMIN_TABLE_PAGE_SIZE_OPTIONS } from 'constants/adminTables';
import { useQueryParamAsLocalState } from 'utils/statelessUpdateUrlParams';
import { DataGridPagination } from '../DataGridPagination';
import { GridActionToolbar, GridActionToolbarProps } from './GridActionToolbar';

const DEFAULT_ROW_HEIGHT = 41.5;
const UNUSED_FILTER_QUERY_PARAM_KEY = '__unusedSettingsDataGridFilterState';
const DEFAULT_COLUMN_FILTER_MODEL: GridFilterModel = { items: [] };

interface PersistedSettingsDataGridFilterState {
  columnFilter: GridFilterModel;
  localSearch: string;
}

const getDefaultPersistedFilterState = (): PersistedSettingsDataGridFilterState => ({
  columnFilter: { ...DEFAULT_COLUMN_FILTER_MODEL },
  localSearch: '',
});

const normalizeColumnFilterModel = (columnFilter?: GridFilterModel | null): GridFilterModel => {
  if (!columnFilter) {
    return { ...DEFAULT_COLUMN_FILTER_MODEL };
  }

  const restColumnFilter = { ...columnFilter };

  delete restColumnFilter.quickFilterValues;

  return {
    ...restColumnFilter,
    items: columnFilter.items || [],
  };
};

const normalizePersistedFilterState = (
  filterState?: PersistedSettingsDataGridFilterState | null
): PersistedSettingsDataGridFilterState => ({
  localSearch: filterState?.localSearch || '',
  columnFilter: normalizeColumnFilterModel(filterState?.columnFilter),
});

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends GridActionToolbarProps {}
}

const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
  event.defaultMuiPrevented = true;
};

const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
  event.defaultMuiPrevented = true;
};

interface SettingsDataGridProps extends DataGridProps {
  handleBulkSave?: () => void;
  bulkSaveText?: string;
  bulkSaveDisabled?: boolean;
  handleAdd?: () => void;
  addText?: string;
  height?: CSSStyleDeclaration['height'];
  initialPageSize?: number;
  error?: any;
  bulkEditMode?: boolean;
  filterStateQueryParamKey?: string;
  showCsvExport?: boolean;
}

const NoRowsErrorOverlay: React.FC<{ error: any }> = ({ error }) => {
  return (
    <GridOverlay
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}
    >
      <ErrorIcon fontSize="large" color="error" />
      <Box sx={{ mt: 2 }}>{error?.message || error || 'Error'}</Box>
    </GridOverlay>
  );
};

export const SettingsDataGrid: React.FC<React.PropsWithChildren<SettingsDataGridProps>> = ({
  handleAdd,
  error,
  rows,
  addText,
  autoHeight = true,
  height = 'auto',
  initialPageSize = ADMIN_TABLE_DEFAULT_PAGE_SIZE,
  bulkEditMode,
  bulkSaveDisabled,
  bulkSaveText,
  handleBulkSave,
  filterStateQueryParamKey,
  showCsvExport,
  ...props
}) => {
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });

  const [persistedFilterState, setPersistedFilterState] =
    useQueryParamAsLocalState<PersistedSettingsDataGridFilterState>(
      filterStateQueryParamKey || UNUSED_FILTER_QUERY_PARAM_KEY,
      withDefault(JsonParam, getDefaultPersistedFilterState())
    );

  const [localSearchState, setLocalSearchState] = React.useState<string>('');
  const [columnFilterState, setColumnFilterState] = React.useState<GridFilterModel>({
    ...DEFAULT_COLUMN_FILTER_MODEL,
  });

  const filterState = filterStateQueryParamKey
    ? normalizePersistedFilterState(persistedFilterState)
    : {
        localSearch: localSearchState,
        columnFilter: normalizeColumnFilterModel(columnFilterState),
      };
  const localSearch = filterState.localSearch;

  const handleLocalSearchChange = React.useCallback(
    (search: string) => {
      if (filterStateQueryParamKey) {
        setPersistedFilterState((currentFilterState) => ({
          ...normalizePersistedFilterState(currentFilterState),
          localSearch: search,
        }));
        return;
      }

      setLocalSearchState(search);
    },
    [filterStateQueryParamKey, setPersistedFilterState]
  );

  const handleColumnFiltersChange = React.useCallback(
    (columnFilter: GridFilterModel) => {
      const normalizedColumnFilter = normalizeColumnFilterModel(columnFilter);

      if (filterStateQueryParamKey) {
        setPersistedFilterState((currentFilterState) => ({
          ...normalizePersistedFilterState(currentFilterState),
          columnFilter: normalizedColumnFilter,
        }));
        return;
      }

      setColumnFilterState(normalizedColumnFilter);
    },
    [filterStateQueryParamKey, setPersistedFilterState]
  );

  const commonDataGridStyles = useSettingsDataGridStyles();

  const sx = {
    ...commonDataGridStyles,
    ...((props.sx || {}) as any),
    ...(bulkEditMode ? bulkEditGridStyles : {}),
  };

  const slots: DataGridProps['slots'] = React.useMemo(
    () => ({
      pagination: DataGridPagination,
      toolbar: GridActionToolbar,
      ...(error ? { noRowsOverlay: () => <NoRowsErrorOverlay error={error} /> } : {}),
      ...props?.slots,
    }),
    [props?.slots, error]
  );

  const slotProps: SettingsDataGridProps['slotProps'] = useMemo(
    () => ({
      ...(props.slotProps || {}),
      toolbar: {
        addText,
        handleAdd,
        localSearch,
        setLocalSearch: handleLocalSearchChange,
        showCsvExport,
        ...(bulkEditMode ? { bulkSaveDisabled, bulkSaveText, handleBulkSave } : {}),
        ...(props.slotProps?.toolbar || {}),
      },
      pagination: {
        SelectProps: {
          sx: { width: 'auto' },
        },
        ...(props.slotProps?.pagination || {}),
      },
    }),
    [
      bulkEditMode,
      bulkSaveText,
      bulkSaveDisabled,
      handleBulkSave,
      addText,
      handleAdd,
      localSearch,
      showCsvExport,
      handleLocalSearchChange,
      props.slotProps,
    ]
  );

  const [debouncedLocalSearch, { isPending }] = useDebounce(localSearch, 100);

  const filterModel = React.useMemo(
    () => ({
      items: [],
      ...filterState.columnFilter,
      quickFilterValues: [debouncedLocalSearch],
    }),
    [debouncedLocalSearch, filterState.columnFilter]
  );

  return (
    <Box height={autoHeight ? 'auto' : height} width="100%">
      <DataGrid
        autoHeight={autoHeight}
        columnHeaderHeight={bulkEditMode ? 2.5 * DEFAULT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT}
        pagination
        editMode="row"
        paginationMode="client"
        pageSizeOptions={ADMIN_TABLE_PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        filterModel={filterModel}
        onFilterModelChange={handleColumnFiltersChange}
        loading={isPending()}
        getRowClassName={({ row }) => (row?.deletedAt ? 'deleted-row' : '')}
        {...props}
        sx={sx}
        slots={slots}
        slotProps={slotProps}
        rows={!error ? rows : []}
      />
    </Box>
  );
};
